/*
    main.js -- Cloud Kickstart main
 */

import Build from '../../config/build.json5'

//  Import the manager class, Vuetify helper and Manager CSS
import {createManager, vuetifyProps} from '@embedthis/devcore'
import '@embedthis/devcore/manager.css'
import '@embedthis/devcore/extras.css'

//  Import the GraphWidget and AWS Cognito authenticator from the managers extras
import {Cognito, GraphWidget} from '@embedthis/devcore/extras'

//  Import core Vue, VueRouter and Vuetity components
import {createApp} from 'vue'
import {createRouter, createWebHashHistory} from 'vue-router'
import {createVuetify} from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

//  Import App REST models from kickstart/ui/src
import * as Models from '@/models/index.js'

//  Import App configuration
//  Backend configuration provided by the cloud service
import Connect from '../connect.js'
import Display from '../theme/display.js'
import Schema from './schema.js'

//  Import Ioto configuration to get the execution profile
import Ioto from '../../config/ioto.json5'

//  Import App VueJS components
import Main from './Main.vue'
import Settings from '@/views/Settings/Settings.vue'
import SettingsReset from '@/views/Settings/SettingsReset.vue'
import SettingsDemo from '@/views/Settings/SettingsDemo.vue'
import VlanEdit from '@/views/Vlans/VlanEdit.vue'
import VlanList from '@/views/Vlans/VlanList.vue'
import Vlans from '@/views/Vlans/Vlans.vue'
import VlanPort from '@/views/Vlans/VlanPort.vue'
import AdminModelList from '@/views/Admin/AdminModelList.vue'
import AdminModel from '@/views/Admin/AdminModel.vue'
import UserEdit from '@/views/Users/UserEdit.vue'
import Users from '@/views/Users/Users.vue'
import UserList from '@/views/Users/UserList.vue'
import EventEdit from '@/views/Events/EventEdit.vue'
import Events from '@/views/Events/Events.vue'
import EventList from '@/views/Events/EventList.vue'
import PortList from '@/views/Ports/PortList.vue'
import Ports from '@/views/Ports/Ports.vue'
import PortVlan from '@/views/Ports/PortVlan.vue'
import PortEdit from '@/views/Ports/PortEdit.vue'

//  Import additional MaterialDesign icons
import {mdiFormatListBulletedSquare} from '@mdi/js'
const Icons = {
    events: mdiFormatListBulletedSquare,
}

async function main() {
    /*
        Initialize Vue.js application components
        - Create Vue instance
        - Set up router with hash-based history
        - Configure Vuetify with components, directives, and theme
    */
    let profile = process.env.PROFILE || Ioto.profile
    let connect = Connect[profile] || Connect

    let vue = createApp(Main)
    let router = createRouter({
        history: createWebHashHistory(),
        routes: [],
    })
    let vuetify = createVuetify(
        vuetifyProps({
            components: components,
            directives: directives,
            icons: Icons,
        })
    )
    vue.use(vuetify)

    let display = await loadDisplay(connect, Display)

    createManager(
        {
            components: {
                //  App VueJS component
                AdminModel,
                AdminModelList,
                EventEdit,
                Events,
                EventList,
                PortEdit,
                PortList,
                PortVlan,
                Ports,
                Settings,
                SettingsReset,
                SettingsDemo,
                VlanEdit,
                VlanList,
                Vlans,
                VlanPort,
                UserEdit,
                Users,
                UserList,
            },
            display: display, //  UI Display definition
            schema: Schema, //  Database schema
            models: Models, //  REST models
            widgets: {graph: GraphWidget}, //  Dashboard widgets
            cognito: Cognito, //  AWS Cognito authenticator
            router, //  VueJS router
            vue, //  Vue instance
            vuetify, //  Vuetify instance
        },
        {
            api: Connect.api, //  Backend cloud service API
            cognito: Connect.cognito, //  Cognito config
            build: Build.build, //  App build number
            host: connect.host, //  Optional override hostname
            profile: profile, //  Execution profile (prod | dev)
            tunnel: 'generic', //  Tunnel requests via 'generic' controller
        }
    )

    /*
        Start VueJS and authenticate the user
        */
    vue.use(router)
    vue.mount('#main')
    router.push({path: '/login'})
}

main()

/*
    Dynamically load the display.js file when running locally
    When using the App designer, the display.js will be updated in the cloud.
    We want to load that rather than the local display.js file.
 */
async function loadDisplay(connect, display) {
    if (connect.display === 'localhost') {
        return display
    }
    if (window.origin.indexOf('localhost') < 0) {
        return display
    }
    if (window.location.search.indexOf('?local') >= 0) {
        return display
    }
    try {
        let host = connect.display || connect.host
        const module = await import(/* @vite-ignore */ `https://${host}/theme/display.js`)
        if (!module?.default) {
            return display
        }
        return module.default
    } catch (err) {
        console.error('Failed to load display.js - using default display', err)
        return display
    }
}
